import DetailPane from "@/views/DetailPane.vue";
import UserProfileInfo from "@/views/userProfile/UserProfileInfo.vue";
import ESpoedSignalRContainer from "@/views/espoed/ESpoedSignalRContainer.vue";
import ESpoedListView from "@/views/espoed/ListView.vue";
import MandateView from "@/views/mandate/MandateView.vue";
import ESpoedReferralView from "@/views/espoed/ReferralView.vue";
import ESpoedSearchView from "@/views/espoed/SearchView.vue";
import ESpoedZisListView from "@/views/espoed/ZisListView.vue";
import { Route, RouteConfig } from "vue-router";

export default class Routes {
  public static constructAll(): RouteConfig[] {
    // Initialize the array with the static routes
    const definedRoutes: RouteConfig[] = [
      this.webappRoutes(), // Add webapp routes
      ...this.zorgIDRoutes(), // Add zorgID routes
      ...this.mandateRoutes(), // Add mandate routes
      ...this.userProfileRoutes(), // Add user profile routes
      this.defaultRoute(), // Add the default route
    ];

    return definedRoutes;
  }

  public static defaultRoute() {
    return {
      path: "*",
      redirect: "/",
    };
  }

  public static webappRoutes() {
    // The regular webapp routes for accessing the full application from the browser
    return {
      path: "/",
      component: ESpoedSignalRContainer,
      props: false,
      children: [
        {
          path: "",
          redirect: "list/-/-/-/-",
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "list",
          redirect: "list/-/-/-/-",
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "list/:cluster/:department",
          redirect: "list/:cluster/:department/-/-",
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "list/:cluster/:department/:type/:priority",
          name: "espoed-list",
          component: ESpoedListView,
          props: true,
          meta: {
            requiresAuth: true,
          },
          children: [
            {
              path: "detail/:referralId",
              name: "referral-list-detail",
              component: DetailPane,
              props: true,
              meta: {
                requiresAuth: true,
                restrictedForPatientTransporters: true,
              },
            },
          ],
        },
        {
          path: "search",
          redirect: "search/-/-/-/-",
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "search/:cluster/:department",
          redirect: "search/:cluster/:department/-/-",
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "search/:cluster/:department/:type/:priority",
          name: "espoed-search",
          meta: {
            requiresAuth: true,
            restrictedForPatientTransporters: true,
          },
          component: ESpoedSearchView,
          props: true,
          children: [
            {
              path: "detail/:referralId",
              name: "espoed-search-detail",
              component: DetailPane,
              props: true,
              meta: {
                requiresAuth: true,
                restrictedForPatientTransporters: true,
              },
            },
          ],
        },
        //Zis routes
        {
          path: "/zis",
          name: "zis-list",
          component: ESpoedZisListView,
          props: (route: Route) => ({ patientNumbers: route.query.pids }),
          meta: {
            requiresAuth: true,
          },
          children: [
            {
              path: "detail/:referralId",
              name: "readonly-list-detail",
              component: DetailPane,
              meta: {
                requiresAuth: true,
                restrictedForPatientTransporters: true,
              },
              props: (route: Route) => ({
                isReadOnly: true,
                patientNumbers: route.query.pids,
                referralId: route.params.referralId,
              }),
            },
          ],
        },
        // Direct route to the readonly detail page (referral-type agnostic)
        {
          path: "referral/:referralId",
          name: "referral-details",
          meta: {
            requiresAuth: true,
            restrictedForPatientTransporters: true,
          },
          component: ESpoedReferralView,
          props: true,
        },
      ],
    };
  }

  static zorgIDRoutes(): any {
    return [
      {
        path: "/ZorgID/Logon",
        name: "ZorgID Logon",
        meta: {
          requiresAuth: true,
        },
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "ZorgID/Logon" */ "./views/zorgID/Logon.vue"),
      },
      {
        path: "/ZorgID/LogonDebug",
        name: "ZorgID Logon (met debug)",
        meta: {
          requiresAuth: true,
        },
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "ZorgID/LogonDebug" */ "./views/zorgID/LogonDebug.vue"),
      },
      {
        path: "/ZorgID/Info",
        name: "ZorgID Info",
        meta: {
          requiresAuth: true,
          requiresZorgIdAuth: true,
        },
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "ZorgID/Info" */ "./views/zorgID/Info.vue"),
      },
      {
        path: "/ZorgID/Logout",
        name: "ZorgID Logout",
        meta: {
          requiresAuth: true,
          requiresZorgIdAuth: true,
        },
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "ZorgID/Logout" */ "./views/zorgID/Logout.vue"),
      },
      {
        path: "/accessdenied",
        name: "AccessDenied",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "accessdenied" */ "./views/zorgID/AccessDenied.vue"),
      },
    ];
  }

  static mandateRoutes(): RouteConfig[] {
    return [
      {
        path: "/mandate",
        component: MandateView,
        props: false,
        meta: {
          requiresProfessionalSummaryEnabled: true,
          requiresAuth: true,
        },
      },
    ];
  }

  static userProfileRoutes(): RouteConfig[] {
    return [
      {
        path: "/userProfile",
        component: UserProfileInfo,
        props: false,
        meta: {
          requiresAuth: true,
        },
      },
    ];
  }
}
